class IFProductPageView {
  static requests = new Map();
  static assets = new Map();
  static url(path, themeId) {
    const url = new URL(path, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid product URL');
    const preview = new URL(location.href).searchParams.get('preview_theme_id') || themeId;
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }
  static async page(url, signal, editor) {
    const key = url.href;
    if (!editor && this.requests.has(key)) return this.requests.get(key);
    const request = fetch(url, { signal, cache: editor ? 'no-store' : 'default', headers: { Accept: 'text/html' } }).then(async response => {
      if (!response.ok) throw new Error('Product page unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!html.querySelector('product-info.if-product-page')) throw new Error('Product template unavailable');
      return html;
    });
    if (!editor) this.requests.set(key, request);
    try { return await request; } catch (error) { this.requests.delete(key); throw error; }
  }
  static remap(root, sourceId, localId) {
    const nodes = [root, ...root.querySelectorAll('*')];
    const ids = new Map();
    nodes.forEach(node => {
      const id = node.getAttribute('id');
      if (id) ids.set(id, id.includes(sourceId) ? id.replaceAll(sourceId, localId) : `${id}-${localId}`);
    });
    nodes.forEach(node => {
      [...node.attributes].forEach(attribute => {
        if (attribute.name.startsWith('data-shopify-editor')) { node.removeAttribute(attribute.name); return; }
        let value = attribute.value.replaceAll(sourceId, localId);
        if (attribute.name === 'id') value = ids.get(attribute.value);
        else if (['for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'data-modal'].includes(attribute.name)) {
          value = attribute.value.split(' ').map(token => {
            const hash = token.startsWith('#') ? '#' : '';
            return hash + (ids.get(token.replace(/^#/, '')) || token.replace(/^#/, '').replaceAll(sourceId, localId));
          }).join(' ');
        }
        node.setAttribute(attribute.name, value);
      });
      if (node.tagName === 'STYLE') node.textContent = node.textContent.replaceAll(sourceId, localId);
    });
    return root;
  }
  static async dependencies(source) {
    source.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
      if (![...document.querySelectorAll('link[rel="stylesheet"]')].some(item => item.href === link.href)) document.head.append(link.cloneNode(true));
    });
    for (const script of source.querySelectorAll('script[src]')) {
      if ([...document.scripts].some(item => item.src === script.src)) continue;
      if (!this.assets.has(script.src)) {
        this.assets.set(script.src, new Promise((resolve, reject) => {
          const asset = document.createElement('script');
          asset.src = script.src;
          asset.onload = resolve;
          asset.onerror = () => { this.assets.delete(script.src); asset.remove(); reject(new Error('Product component unavailable')); };
          document.head.append(asset);
        }));
      }
      await this.assets.get(script.src);
    }
  }
  static purchaseOnly(view) {
    const info = view.querySelector('.product__info-container');
    if (!info) return;
    const purchaseBlocks = '.if-product-title, .if-product-price, .if-product-variants, .if-product-quantity, .if-product-buy, .product__inventory, .product__sku, .product__tax, .volume-pricing-note, style, script';
    [...info.children].forEach(block => {
      if (!block.matches(purchaseBlocks) && !block.querySelector('form[id^="product-form-installment-"]')) block.remove();
    });
  }
}
window.IFProductPageView = IFProductPageView;
if (!customElements.get('if-featured-products')) {
  customElements.define('if-featured-products', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.slots = [...this.querySelectorAll('[data-product-page-view]')];
      this.slots.forEach(slot => this.load(slot));
      this.querySelectorAll('[data-product-retry]').forEach(button => button.addEventListener('click', () => this.load(button.closest('[data-product-page-view]')), options));
      window.addEventListener('focus', () => {
        if (this.dataset.editor === 'true' && !this.contains(document.activeElement)) this.slots.forEach(slot => this.load(slot));
      }, options);
    }
    async load(slot) {
      if (slot.dataset.loading === 'true') return;
      slot.dataset.loading = 'true';
      slot.setAttribute('aria-busy', 'true');
      const error = slot.querySelector('[data-product-load-error]');
      if (error) error.hidden = true;
      try {
        const themeId = this.dataset.editor === 'true' ? this.dataset.themeId : '';
        const url = IFProductPageView.url(slot.dataset.productUrl, themeId);
        const html = await IFProductPageView.page(url, this.controller.signal, this.dataset.editor === 'true');
        const source = html.querySelector('product-info.if-product-page');
        const sourceId = source.dataset.section;
        const localId = `${this.dataset.section}-product-${slot.dataset.productId}`;
        await IFProductPageView.dependencies(source);
        if (!this.isConnected) return;
        const view = IFProductPageView.remap(source.cloneNode(true), sourceId, localId);
        IFProductPageView.purchaseOnly(view);
        view.dataset.sourceSection = sourceId;
        view.dataset.previewTheme = url.searchParams.get('preview_theme_id') || '';
        view.dataset.updateUrl = 'false';
        view.classList.add('if-featured__page-view');
        view.querySelectorAll('if-product-sticky, script[src], script[type="application/ld+json"]').forEach(node => node.remove());
        view.querySelectorAll('product-modal, modal-dialog').forEach(node => node.dataset.ifFeaturedOwner = localId);
        document.querySelectorAll(`[data-if-featured-owner="${CSS.escape(localId)}"]`).forEach(node => node.remove());
        const content = document.createElement('div');
        content.id = `shopify-section-${localId}`;
        content.className = 'if-featured__page-content';
        content.innerHTML = view.outerHTML;
        const target = slot.querySelector('[data-product-page-content]');
        if (slot === this.slots[0]) document.getElementById(`ProductModal-${this.dataset.section}`)?.remove();
        target.replaceChildren(content);
        slot.dataset.loaded = 'true';
        delete slot.dataset.loadError;
        this.querySelector('if-landing-carousel')?.update();
        window.Shopify?.PaymentButton?.init();
        window.ProductModel?.loadShopifyXR();
      } catch (failure) {
        slot.dataset.loadError = failure.message;
        if (failure.name !== 'AbortError' && error) error.hidden = false;
      } finally {
        slot.dataset.loading = 'false';
        slot.removeAttribute('aria-busy');
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.slots?.forEach(slot => {
        const owner = `${this.dataset.section}-product-${slot.dataset.productId}`;
        document.querySelectorAll(`[data-if-featured-owner="${CSS.escape(owner)}"]`).forEach(node => node.remove());
      });
    }
  });
}
